import { SingleIssueEvent } from '@/routes/projectStages/issue/types/issue.ts'
import { EventTypeT } from '@/routes/projectStages/issue/view/activity/EventType.tsx'
import { formatDateTime } from '@/lib/utils.ts'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import { TFunction } from 'i18next'
import createIri from '@/lib/helpers/createIri.ts'
import { formatUserName } from '@/lib/helpers/formatUserName.ts'

type GetEventStringsStringT = {
  stringType: 'bold' | 'normal'
  string: string | undefined | null
}

export default function getEventString(
  event: SingleIssueEvent,
  projectUsers: ProjectStagesUsersT | undefined | null,
  t: TFunction<'translation', undefined>,
  currentUserId: number | null | undefined,
): Array<GetEventStringsStringT> {
  const eventType = event?.eventType as EventTypeT
  if (!eventType) return []
  // console.log(event)

  const creator = projectUsers?.find(user => user?.[`@id`] === event.createdBy)
  const recipient = projectUsers?.find(user => user['@id'] === event.valueUsers?.[0])
  const currentUserIri = createIri(`users`, currentUserId)
  let creatorName = `${formatUserName(creator)} (${creator?.projectOrganization})`

  const creatorString = boldString(creatorName)
  const toIssueString = normalString(t('project.issue.eventTypes.common.toIssue')) //  "zgłoszenia",
  const forString = normalString(t('project.issue.eventTypes.common.to')) // 'na'
  const toString = normalString(t('project.issue.eventTypes.common.to2')) // 'do'
  const fromString = normalString(t('project.issue.eventTypes.common.from')) // 'ze'
  const changedString = normalString(t('project.issue.eventTypes.common.changed')) // zmienił
  const deletedLabelString = normalString(t('project.issue.eventTypes.common.deletedLabelShort'))
  const typeString = normalString(t('project.issue.eventTypes.common.type')) // typu
  const addedLabelString = normalString(t('project.issue.eventTypes.common.addedLabelShort')) // dodał etykietę
  const inString = normalString(t('project.issue.eventTypes.common.in')) // za
  const mentionedString = normalString(t('project.issue.eventTypes.you.mentioned'))
  const removedYouFromIssueString = normalString(t(`project.issue.eventTypes.you.taskRemoved`))
  const assignedYouAnIssue = normalString(t(`project.issue.eventTypes.you.issue.recipient.changed`))

  const dynamicString = normalString(t(`project.issue.eventTypes.${eventType}`))
  const isCurrentUser = event.createdBy === currentUserIri

  if (isCurrentUser && eventType !== 'issue.recipient.changed') {
    const dynamicString = normalString(t(`project.issue.eventTypes.you.${eventType}`))
    return [dynamicString]
  }

  switch (eventType) {
    case 'issue.labels.deleted': {
      if (!event.valueString) return [deletedLabelString]
      const values = event.valueString.split('|')
      return [
        creatorString,
        deletedLabelString,
        boldString(values[1]),
        typeString,
        boldString(`${values[0]}`),
        fromString,
        toIssueString,
      ]
    }

    case 'issue.labels.added': {
      if (!event.valueString) return [addedLabelString]
      const values = event.valueString.split('|')

      return [
        creatorString,
        addedLabelString,
        boldString(`${values[1]}`),
        typeString,
        boldString(values[0]),
        toString,
        toIssueString,
      ]
    }

    case 'issue.status.changed': {
      if (!event.valueString) return []
      const values = event.valueString.split('|')
      return [creatorString, changedString, boldString(values[0]), forString, boldString(values[1])]
    }

    case 'issue.duedate.changed':
      return [
        creatorString,
        dynamicString,
        forString,
        boldString(formatDateTime(event.valueDate, { excludeTime: true })),
      ]

    case 'issue.duedate.approaching':
      if (!event.valueInteger) return [dynamicString]
      return [
        dynamicString,
        inString,
        boldString(String(event.valueInteger)),
        normalString(t('project.issue.eventTypes.common.days')),
      ]

    case 'issue.duedate.passed':
      if (!event.valueInteger) return [dynamicString]

      return [
        creatorString,
        dynamicString,
        boldString(String(event.valueInteger)),
        normalString(t('project.issue.eventTypes.common.ago')),
      ]

    case 'issue.recipient.changed':
      if (event.valueUsers && event.valueUsers[0] === currentUserIri) {
        return [creatorString, assignedYouAnIssue]
      }
      // you were removed from the task
      return [creatorString, removedYouFromIssueString]

    case 'issue.comments.updated':
    case 'issue.comments.created':
      if (recipient) {
        return [creatorString, mentionedString]
      }
      return [creatorString, dynamicString]

    default:
      return [creatorString, dynamicString]
  }
}

function boldString(string: string | null | undefined): GetEventStringsStringT {
  return {
    stringType: 'bold',
    string,
  }
}
function normalString(string: string | null | undefined): GetEventStringsStringT {
  return {
    stringType: 'normal',
    string,
  }
}
